"use client";

import { Calendar, ChevronLeft, Clock, Tag, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function formatDate(dateString?: string): string {
  if (!dateString) return "";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="space-y-6 animate-pulse">
          <div className="h-4 w-32 bg-slate-800 rounded" />
          <div className="h-12 w-3/4 bg-slate-800 rounded" />
          <div className="h-6 w-1/2 bg-slate-800 rounded" />
          <div className="h-96 w-full bg-slate-800 rounded-lg" />
          <div className="space-y-3">
            <div className="h-4 w-full bg-slate-800 rounded" />
            <div className="h-4 w-full bg-slate-800 rounded" />
            <div className="h-4 w-5/6 bg-slate-800 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 text-center">
        <p className="text-slate-400">Failed to load intelligence report.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 text-center">
        <p className="text-slate-400">Report not found.</p>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "TiaLinx Command";
  const authorAvatar = post.author?.avatar?.["96"] || post.author?.avatar?.["48"] || "";

  return (
    <article className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
      {}
      <div className="mb-8">
        <Link
          to="/HomePage"
          className="inline-flex items-center gap-2 text-sm font-default text-slate-400 hover:text-blue-300 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Return to Command Center</span>
        </Link>
      </div>

      {}
      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {post.categories.map((cat, i) => (
            <Link
              key={cat.id}
              to={cat.link || "#"}
              data-index={i}
              className="inline-flex items-center px-3 py-1 text-xs font-bold tracking-widest uppercase font-default bg-slate-800 text-blue-400 border border-slate-700"
            >
              {cat.name}
            </Link>
          ))}
        </div>
      )}

      {}
      <h1 className="font-default font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-white mb-6">
        {post.title}
      </h1>

      {}
      {post.excerpt && (
        <div
          className="font-default text-xl sm:text-2xl text-slate-300 leading-relaxed mb-8"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      {}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-default text-slate-400 mb-10">
        <div className="flex items-center gap-2">
          <Avatar className="h-8 w-8 border border-slate-700">
            {authorAvatar ? (
              <AvatarImage src={authorAvatar} alt={authorName} />
            ) : null}
            <AvatarFallback className="bg-slate-800 text-blue-400 text-xs">
              {authorName.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <span className="text-white">Authored by {authorName}</span>
        </div>
        {post.date && (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>{formatDate(post.date)}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>Secure Briefing</span>
        </div>
      </div>

      <Separator className="mb-10 border-slate-700" />

      {}
      {post.featuredImage && (
        <figure className="mb-12 -mx-4 sm:mx-0">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-cover border border-slate-700 shadow-lg"
          />
        </figure>
      )}

      {}
      {post.content && (
        <div
          className="prose prose-lg max-w-none font-default text-slate-300 leading-relaxed [&>p]:mb-6 [&>p]:text-base [&>p]:sm:text-lg [&>p]:leading-[1.8] [&>h2]:font-default [&>h2]:text-3xl [&>h2]:sm:text-4xl [&>h2]:font-bold [&>h2]:mt-12 [&>h2]:mb-5 [&>h2]:text-white [&>h3]:font-default [&>h3]:text-2xl [&>h3]:sm:text-3xl [&>h3]:font-bold [&>h3]:mt-10 [&>h3]:mb-4 [&>h3]:text-white [&>h4]:font-default [&>h4]:text-xl [&>h4]:font-semibold [&>h4]:mt-8 [&>h4]:mb-3 [&>h4]:text-white [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul]:space-y-2 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol]:space-y-2 [&>blockquote]:border-l-4 [&>blockquote]:border-blue-600 [&>blockquote]:pl-6 [&>blockquote]:font-default [&>blockquote]:text-xl [&>blockquote]:my-8 [&>blockquote]:text-blue-200 [&>img]:border [&>img]:border-slate-700 [&>img]:my-8 [&>img]:w-full [&_a]:text-blue-400 [&_a]:underline [&_a]:underline-offset-4 [&_a]:decoration-blue-600 hover:[&_a]:decoration-white [&_strong]:text-white [&_strong]:font-bold"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-slate-700">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 text-sm font-bold font-default text-slate-400">
              <Tag className="h-4 w-4" />
              Classification Tags:
            </span>
            {post.tags.map((tag, i) => (
              <Link
                key={tag.id}
                to={tag.link || "#"}
                data-index={i}
                className="inline-flex items-center px-3 py-1 text-xs font-default bg-slate-800 text-slate-300 border border-slate-700 hover:bg-blue-900 hover:text-white transition-colors"
              >
                #{tag.slug}
              </Link>
            ))}
          </div>
        </div>
      )}

      <Separator className="my-12 border-slate-700" />

      {}
      <Card className="bg-slate-900 border border-slate-700 shadow-md">
        <CardContent className="px-6">
          <div className="flex flex-col sm:flex-row gap-6 items-start">
            <Avatar className="h-20 w-20 shrink-0 border border-slate-700">
              {authorAvatar ? (
                <AvatarImage src={authorAvatar} alt={authorName} />
              ) : null}
              <AvatarFallback className="bg-slate-800 text-blue-400 text-2xl font-default">
                {authorName.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-default text-blue-400 mb-2">
                <User className="h-3 w-3" />
                <span>Intelligence Lead</span>
              </div>
              <h3 className="font-default text-2xl font-bold text-white mb-2">
                {authorName}
              </h3>
              <p className="font-default text-sm sm:text-base text-slate-400 leading-relaxed">
                Senior analyst at TiaLinx, Inc., specializing in orbital intelligence, deep space surveillance, and advanced signal processing systems.
              </p>
              {post.author?.link && (
                <Link to={post.author.link} className="inline-block mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="font-default text-xs uppercase tracking-widest border-slate-700 text-white hover:bg-blue-900"
                  >
                    View all reports
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </article>
  );
}

function RelatedPostsConsumer({ currentPostId }: { currentPostId?: string }) {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="space-y-3 animate-pulse">
            <div className="aspect-[4/3] bg-slate-800" />
            <div className="h-4 w-3/4 bg-slate-800" />
            <div className="h-4 w-1/2 bg-slate-800" />
          </div>
        ))}
      </div>
    );
  }

  const related = posts.filter((p) => p.id !== currentPostId).slice(0, 3);

  if (related.length === 0) {
    return (
      <p className="text-center font-default text-slate-500">
        No additional intelligence reports available.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
      {related.map((post, i) => (
        <article
          key={post.id}
          data-index={i}
          className="group flex flex-col"
        >
          <Link to={post.link || "#"} className="block overflow-hidden border border-slate-700 mb-4">
            {post.featuredImage ? (
              <img
                src={post.featuredImage}
                alt={post.title}
                loading="lazy"
                decoding="async"
                className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            ) : (
              <div className="aspect-[4/3] w-full bg-slate-800 flex items-center justify-center">
                <span className="font-default text-4xl text-slate-600">TiaLinx</span>
              </div>
            )}
          </Link>
          {post.categories && post.categories.length > 0 && (
            <span className="text-xs uppercase tracking-widest font-default text-blue-400 mb-2">
              {post.categories[0].name}
            </span>
          )}
          <h3 className="font-default text-xl sm:text-2xl font-bold text-white leading-snug mb-3">
            <Link
              to={post.link || "#"}
              className="hover:text-blue-300 transition-colors"
            >
              {post.title}
            </Link>
          </h3>
          {post.date && (
            <span className="mt-auto text-xs font-default text-slate-500">
              {formatDate(post.date)}
            </span>
          )}
        </article>
      ))}
    </div>
  );
}

function CurrentPostIdAndRelated() {
  const { posts } = usePosts();
  const currentId = posts && posts.length > 0 ? posts[0].id : undefined;
  const categorySlug =
    posts && posts.length > 0 && posts[0].categories && posts[0].categories.length > 0
      ? posts[0].categories[0].slug
      : undefined;

  const relatedQueryParams: Record<string, any> = {
    post_type: "post",
    per_page: 4,
    orderby: "date",
    order: "desc",
  };
  if (categorySlug) {
    relatedQueryParams.category_slug = categorySlug;
  }
  const relatedQuery = new WP_Query(relatedQueryParams);

  return (
    <div className="bg-slate-950 border-t border-slate-800">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="text-center mb-12">
          <span className="inline-block text-xs uppercase tracking-[0.3em] font-default text-blue-500 mb-4">
            Intelligence Stream
          </span>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Related Reports
          </h2>
        </div>
        <PostsProvider wp_query={relatedQuery}>
          <RelatedPostsConsumer currentPostId={currentId} />
        </PostsProvider>
      </div>
    </div>
  );
}

export default function Post() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : { post_type: "post" };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="3123"
      id="single-post-template-section"
      className="bg-[#080F1A] text-white"
    >
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
        <CurrentPostIdAndRelated />
      </PostsProvider>
    </section>
  );
}